import React from 'react';
import { useNavigate } from 'react-router-dom';
import './landing.css';
import { LinkedinOutlined, XOutlined, YoutubeOutlined, InstagramOutlined } from '@ant-design/icons';

const HERO_STATS = [
  { value: '2.1M+', label: 'Sessions analyzed monthly' },
  { value: '+38%', label: 'Conversion lift after 60 days' },
  { value: '<5min', label: 'Average setup time' },
  { value: '99.9%', label: 'Uptime guarantee' }
];

const FEATURES = [
  {
    icon: '📊',
    title: 'Real-time Analytics',
    description: 'Monitor your website performance in real-time with live visitor tracking, instant metrics updates, and comprehensive dashboards that give you complete visibility into user behavior.'
  },
  {
    icon: '🎯',
    title: 'Conversion Tracking',
    description: 'Track every step of the customer journey with advanced conversion funnels, goal tracking, and attribution modeling to optimize your marketing ROI and drive growth.'
  },
  {
    icon: '🔥',
    title: 'Heat Maps & Behavior',
    description: 'Visualize user interactions with heat maps, scroll tracking, and click analysis to understand how users engage with your content and identify optimization opportunities.'
  },
  {
    icon: '📱',
    title: 'Multi-platform Support',
    description: 'Seamlessly integrate with WordPress, Wix, Shopify, and 13+ major platforms. Our lightweight snippet ensures fast performance across all your digital properties.'
  },
  {
    icon: '🔒',
    title: 'Privacy-First Analytics',
    description: 'Built with privacy at our core. We respect user privacy while providing valuable insights, ensuring compliance with GDPR, CCPA, and other privacy regulations.'
  },
  {
    icon: '⚡',
    title: 'Lightning Fast',
    description: 'Our optimized tracking script loads in under 100ms, ensuring your website performance remains uncompromised while capturing comprehensive analytics data.'
  }
];

const VALUES = [
  {
    icon: '💡',
    title: 'Innovation',
    description: 'We constantly push the boundaries of what\'s possible in analytics, bringing cutting-edge technology and methodologies to help you stay ahead of the competition.'
  },
  {
    icon: '🎨',
    title: 'Simplicity',
    description: 'Complex analytics made simple. We believe powerful insights should be accessible to everyone, regardless of technical expertise or data science background.'
  },
  {
    icon: '🤝',
    title: 'Partnership',
    description: 'We\'re more than a tool provider – we\'re your growth partner. Your success is our success, and we\'re committed to helping you achieve your business goals.'
  }
];


function Header() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <>
      <header className="hdr container">
        <div className="brand" onClick={() => navigate('/')} style={{ cursor: 'pointer', fontSize: '24px', fontWeight: '700', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/headerlogo.png" alt="Asuma Logo" style={{ height: '32px', width: 'auto' }} />
            <span className="logo-text">Asuma</span>
          </div>
          <div className="logo-tagline" style={{ fontSize: '12px', fontWeight: '400', marginTop: '-2px', fontStyle: 'italic' }}>Customer intelligence for growing businesses.</div>
        </div>
        <nav className="nav">
          <button type="button" onClick={() => navigate('/')}>Home</button>
          <button type="button" onClick={() => { navigate('/'); setTimeout(() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }), 100); }}>Features</button>
          <button type="button" onClick={() => { navigate('/'); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100); }}>Pricing</button>
        </nav>
        <div className="hdr-actions">
          <button className="btn btn-ghost" type="button" onClick={() => navigate('/login')}>Log in</button>
          <button className="btn btn-primary hdr-cta" type="button" onClick={() => navigate('/signup')}>Start Free Trial</button>
        </div>
        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </header>

      {mobileMenuOpen && (
        <div className="mobile-menu">
          <button type="button" onClick={() => { navigate('/'); setMobileMenuOpen(false); }}>Home</button>
          <button type="button" onClick={() => { navigate('/'); setMobileMenuOpen(false); setTimeout(() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }), 100); }}>Features</button>
          <button type="button" onClick={() => { navigate('/'); setMobileMenuOpen(false); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100); }}>Pricing</button>
          <button type="button" onClick={() => navigate('/login')}>Log in</button>
          <button className="btn btn-primary" type="button" onClick={() => navigate('/signup')}>Start Free Trial</button>
        </div>
      )}
    </>
  );
}

function HeroSection() {
  return (
    <section style={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '80px 20px',
      textAlign: 'center',
      color: 'white'
    }}>
      <div className="container">
        <h1 style={{
          fontSize: '3.5rem',
          fontWeight: '700',
          marginBottom: '20px',
          background: 'linear-gradient(45deg, #fff, #e0e7ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          About Asuma
        </h1>
        <p style={{
          fontSize: '1.3rem',
          marginBottom: '40px',
          opacity: 0.95,
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Empowering businesses with intelligent customer analytics and actionable insights to drive growth and enhance user experiences.
        </p>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '0 20px 60px'
    }}>
      <div className="container">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '30px'
        }}>
          {HERO_STATS.map((stat) => (
            <div key={stat.label} style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              padding: '30px',
              textAlign: 'center',
              color: 'white',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '10px', color: '#fbbf24' }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '1rem', opacity: 0.9 }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  return (
    <section style={{
      background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
      borderRadius: '20px',
      padding: '60px',
      margin: '40px 20px',
      textAlign: 'center'
    }}>
      <div className="container">
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#1e293b',
          marginBottom: '30px'
        }}>
          Our Mission
        </h2>
        <p style={{
          fontSize: '1.2rem',
          color: '#475569',
          maxWidth: '800px',
          margin: '0 auto',
          lineHeight: '1.8'
        }}>
          At Asuma, we believe that every business deserves access to powerful analytics that drive meaningful growth. 
          We're on a mission to democratize customer intelligence, making it accessible, actionable, and affordable for 
          businesses of all sizes. Our platform transforms raw data into strategic insights that help you understand 
          your customers better and make data-driven decisions with confidence.
        </p>
      </div>
    </section>
  );
}

function WhatWeDoSection() {
  return (
    <section style={{
      background: 'white',
      borderRadius: '20px',
      padding: '60px',
      margin: '40px 20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)'
    }}>
      <div className="container">
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#1e293b',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          What We Do
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: '1.1rem',
          color: '#64748b',
          marginBottom: '40px'
        }}>
          Asuma provides comprehensive web analytics solutions that help businesses understand, engage, and convert their audience effectively.
        </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '30px',
          marginTop: '50px'
        }}>
          {FEATURES.map((feature) => (
            <div key={feature.title} style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '30px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                fontSize: '1.5rem'
              }}>
                {feature.icon}
              </div>
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#1e293b',
                marginBottom: '15px'
              }}>
                {feature.title}
              </h3>
              <p style={{
                color: '#64748b',
                lineHeight: '1.7'
              }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  return (
    <section style={{
      background: 'white',
      borderRadius: '20px',
      padding: '60px',
      margin: '40px 20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)'
    }}>
      <div className="container">
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#1e293b',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          Our Values
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '30px',
          marginTop: '50px'
        }}>
          {VALUES.map((value) => (
            <div key={value.title} style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '30px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                fontSize: '1.5rem'
              }}>
                {value.icon}
              </div>
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#1e293b',
                marginBottom: '15px'
              }}>
                {value.title}
              </h3>
              <p style={{
                color: '#64748b',
                lineHeight: '1.7'
              }}>
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function CTASection() {
  const navigate = useNavigate();
  
  return (
    <section style={{
      textAlign: 'center',
      padding: '80px 20px',
      background: 'linear-gradient(135deg, #1e293b, #334155)',
      borderRadius: '20px',
      margin: '60px 20px',
      color: 'white'
    }}>
      <div className="container">
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          marginBottom: '20px'
        }}>
          Ready to Transform Your Analytics?
        </h2>
        <p style={{
          fontSize: '1.2rem',
          marginBottom: '40px',
          opacity: 0.9,
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Join thousands of businesses that trust Asuma to deliver actionable insights and drive meaningful growth.
        </p>
        <div style={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/signup')}
            style={{
              padding: '15px 30px',
              fontSize: '1rem'
            }}
          >
            Start Free Trial
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => window.dispatchEvent(new CustomEvent('open-contact-modal'))}
            style={{
              padding: '15px 30px',
              fontSize: '1rem',
              background: 'transparent',
              border: '2px solid white'
            }}
          >
            Schedule Demo
          </button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="ftr" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.8) 0%, rgba(139,92,246,0.7) 50%, rgba(168,85,247,0.6) 100%), rgba(4,7,18,0.1)' }}>
      <div className="container">
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 1fr 1fr 1fr', 
          gap: '40px', 
          alignItems: 'start',
          padding: '40px 0'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <img src="/headerlogo.png" alt="Asuma Logo" style={{ height: '28px', width: 'auto' }} />
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#000000', margin: 0 }}>Asuma</h3>
            </div>
            <p style={{ color: '#000000', fontSize: '14px', marginTop: '8px', marginBottom: '16px' }}>Customer intelligence for growing businesses.</p>
            <p className="muted">© {new Date().getFullYear()} Asuma, Inc. All rights reserved.</p>
          </div>
          <div className="ftr-links">
            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#000000', fontWeight: '600' }}>Company</h4>
            <div style={{ width: '100%', height: '1px', background: 'rgba(255, 255, 255, 0.2)', marginBottom: '12px' }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span type="button" onClick={() => window.location.href = '/about'} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>About</span>
              <span type="button" onClick={() => window.open('/privacy-policy.html', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>Privacy Policy</span>
              <span type="button" onClick={() => window.open('/terms-and-conditions.html', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>Terms & Conditions</span>
              <span type="button" onClick={() => alert('White Label coming soon')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>White Label</span>
              <span type="button" onClick={() => alert('Blog coming soon')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>Blog</span>
            </div>
          </div>
          <div className="ftr-links">
            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#000000', fontWeight: '600' }}>Contact</h4>
            <div style={{ width: '100%', height: '1px', background: 'rgba(255, 255, 255, 0.2)', marginBottom: '12px' }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span type="button" onClick={() => window.location.href = 'mailto:support@asumahq.com'} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>support@asumahq.com</span>
            </div>
          </div>
          <div className="ftr-links">
            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#000000', fontWeight: '600' }}>Follow Us</h4>
            <div style={{ width: '100%', height: '1px', background: 'rgba(255, 255, 255, 0.2)', marginBottom: '12px' }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span type="button" onClick={() => window.open('https://www.linkedin.com/company/asumahq/', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LinkedinOutlined style={{ fontSize: '16px', color: '#0077B5' }} />
                @asumahq
              </span>
              <span type="button" onClick={() => window.open('https://x.com/AsumaHQ', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <XOutlined style={{ fontSize: '16px', color: '#000000' }} />
                @AsumaHQ
              </span>
              <span type="button" onClick={() => window.open('https://www.youtube.com/@AsumaHQ', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <YoutubeOutlined style={{ fontSize: '16px', color: '#FF0000' }} />
                @AsumaHQ
              </span>
              <span type="button" onClick={() => window.open('https://instagram.com/asumahq', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InstagramOutlined style={{ fontSize: '16px', color: '#E4405F' }} />
                @asumahq
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function AboutPage() {
  React.useEffect(() => {
    document.body.classList.add('light-mode');
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <HeroSection />
      <StatsSection />
      <MissionSection />
      <WhatWeDoSection />
      <ValuesSection />
      <CTASection />
      <Footer />
    </>
  );
}